import { formatCount } from "../../../../core/helpers/count-formatting-util.js";
import { router } from "@foretagsplatsen/widgetjs";
import AbstractFormulaVisitor from "./AbstractFormulaVisitor.js";
import AdditionFormulaNode from "../AdditionFormulaNode.js";
import SumFormulaNode from "../SumFormulaNode.js";
import TextRepresentationAccountSpecificationVisitor from "../../../accountSpecification/visitors/TextRepresentationAccountSpecificationVisitor.js";
import TextRepresentationDimensionSpecificationVisitor from "../../../dimensionSpecification/visitors/TextRepresentationDimensionSpecificationVisitor.js";

/**
 * `AbstractMathFormulaVisitor` is a formula visitor used to visit an
 * AbstractFormulaNode. It outputs a textual representation of a
 * mathematical formula.
 *
 * @abstract
 * @param {boolean} [spec.useAnchors = true] - Toggle whether to
 *   display links to key figures.
 * @param {Report} [report] - If the formula contains instances of
 *   ReportReferenceFormulaNode, this variable is used to get the
 *   referenced report item.
 * @param {dimension} [spec.dimensionTree] - Root dimension from which
 *   dimension ids are resolved.
 */
export default class AbstractMathFormulaVisitor extends AbstractFormulaVisitor {
	constructor({ useAnchors = true, report, dimensionTree } = {}) {
		super(...arguments);
		this._useAnchors = useAnchors;
		this._report = report;
		this._dimensionTree = dimensionTree;
	}

	// Visitor

	visitSumNode(node) {
		const operands = node.getOperands();

		switch (operands.length) {
			case 0: {
				return "0";
			}
			case 1: {
				return this.visit(operands[0]);
			}
			default: {
				return this.visit(
					new AdditionFormulaNode({
						leftOperand: operands[0],
						rightOperand: new SumFormulaNode({
							operands: operands.slice(1),
						}),
					}),
				);
			}
		}
	}

	visitAdditionNode(node) {
		return this._add({
			leftOperand: this.visit(node.getLeftOperand()),
			rightOperand: this.visit(node.getRightOperand()),
		});
	}

	visitSubtractionNode(node) {
		// Subtraction is left-associative, so we need to add parentheses to the subtrahend if it has
		// same precedence (i.e. it is an addition or a subtraction)
		let subtrahend = this.visit(node.getRightOperand());
		if (node.getRightOperand().precedence() === node.precedence()) {
			subtrahend = this._wrapInParenthesis(subtrahend);
		}

		return this._subtract({
			leftOperand: this.visit(node.getLeftOperand()),
			rightOperand: subtrahend,
		});
	}

	visitMultiplicationNode(node) {
		const multiplicand = this._visitAndWrapInParenthesesIfNeeded({
			node: node.getLeftOperand(),
			parent: node,
		});

		const multiplier = this._visitAndWrapInParenthesesIfNeeded({
			node: node.getRightOperand(),
			parent: node,
		});

		return this._multiply({ multiplicand, multiplier });
	}

	visitDivisionNode(node) {
		const dividend = this._visitAndWrapInParenthesesIfNeeded({
			node: node.getLeftOperand(),
			parent: node,
		});

		const divisor = this._visitAndWrapInParenthesesIfNeeded({
			node: node.getRightOperand(),
			parent: node,
		});

		return this._divide({ dividend, divisor });
	}

	visitNegatedNode(node) {
		const expression = this._visitAndWrapInParenthesesIfNeeded({
			node: node.getExpression(),
			parent: node,
		});

		return this._negated({ expression });
	}

	visitClosingBalanceNode(node) {
		return this._periodNode("ClosingBalance", node);
	}

	visitAccountNode(node) {
		return this._text(
			new TextRepresentationAccountSpecificationVisitor().visit(
				node.getAccountSpecification(),
			),
		);
	}

	visitBalanceNode(node) {
		return this._periodNode("Balance", node);
	}

	visitChangeNode(node) {
		return this._periodNode("Change", node);
	}

	visitOpeningBalanceNode(node) {
		return this._periodNode("OpeningBalance", node);
	}

	visitYearOpeningBalanceNode(node) {
		return this._periodNode("YearOpeningBalance", node);
	}

	visitComponentNode(node) {
		return this._text(node.getName());
	}

	visitConstantNode(node) {
		return formatCount(node.getValue());
	}

	visitDataNode(node) {
		return this._textWithOptions({
			text: node.getValueType(),
			options: [this._text(node.getName())],
		});
	}

	visitKeyFigureNode(node) {
		const keyFigure = node.getKeyFigure();
		const text = this._text(keyFigure.getName());

		if (!node.isVisible() || !this._useAnchors) {
			return text;
		}

		const href = router.getRouter().linkToParameters({
			keyFigure: keyFigure.getId(),
		});

		return this._link({ href, text });
	}

	visitNaNode() {
		return this._text("N/A");
	}

	visitOrNode(node) {
		const leftOperand = this._visitAndWrapInParenthesesIfNeeded({
			node: node.getExpression(),
			parent: node,
		});

		const rightOperand = node.getFallBackValue();

		return this._or({ leftOperand, rightOperand });
	}

	visitPreviousPeriodNode(node) {
		return this._previousPeriod({
			text: "PreviousPeriod",
			numberOfPeriods: node.getNumPeriods(),
			expression: node.getExpression(),
		});
	}

	visitPreviousYearNode(node) {
		return this._previousPeriod({
			text: "PreviousYear",
			numberOfPeriods: node.getNumYears(),
			expression: node.getExpression(),
		});
	}

	visitDimensionNode(node) {
		return this._textWithOptions({
			text: "Dimension",
			options: [
				this.visit(node.getExpression()),
				this._dimensionSpecificationText(
					node.getDimensionSpecification(),
				),
			],
		});
	}

	visitPeriodShiftFormulaNode(node) {
		const type = node.getShiftPeriodType()?.getName();
		const typeText = type ? `, ${this._text(type)}` : "";

		return this._textWithOptions({
			text: "PeriodShift",
			options: [
				this.visit(node.getExpression()),
				`${this._text(node.getShift())}${typeText}`,
			],
		});
	}

	visitPeriodChangeFormulaNode(node) {
		return this._textWithOptions({
			text: "PeriodChange",
			options: [
				this.visit(node.getExpression()),
				this._text(node.getPeriodType()?.getName()),
			],
		});
	}

	visitPeriodRollingFormulaNode(node) {
		return this._textWithOptions({
			text: "Rolling",
			options: [
				this.visit(node.getExpression()),
				this._text(node.getRollingLength()),
				this._text(node.getPeriodType()?.getName()),
			],
		});
	}

	visitReportReferenceNode(node) {
		const reportItem = this._report.findItemWithTemplate(
			node.getReference(),
		);

		const label = reportItem
			? reportItem.getTitle()
			: node.getReferenceLabel();

		return this._text(label);
	}

	_add({ _leftOperand, _rightOperand }) {
		return this._subclassResponsibility();
	}

	_subtract({ _leftOperand, _rightOperand }) {
		return this._subclassResponsibility();
	}

	_multiply({ _multiplicand, _multiplier }) {
		return this._subclassResponsibility();
	}

	_divide({ _dividend, _divisor }) {
		return this._subclassResponsibility();
	}

	_negated({ _expression }) {
		return this._subclassResponsibility();
	}

	_text(_text) {
		return this._subclassResponsibility();
	}

	_link({ _href, _text }) {
		return this._subclassResponsibility();
	}

	_or({ _leftOperand, _rightOperand }) {
		return this._subclassResponsibility();
	}

	_visitAndWrapInParenthesesIfNeeded({ node, parent }) {
		let text = this.visit(node);
		if (node.precedence() < parent.precedence()) {
			text = this._wrapInParenthesis(text);
		}
		return text;
	}

	_periodNode(text, node) {
		return this._textWithOptions({
			text,
			options: [this.visit(node.getExpression())],
		});
	}

	_previousPeriod({ text, numberOfPeriods, expression }) {
		const options = [this.visit(expression)];
		if (numberOfPeriods > 1) options.push(numberOfPeriods);

		return this._textWithOptions({ text, options });
	}

	_wrapInParenthesis(_expression) {
		return this._subclassResponsibility();
	}

	_dimensionSpecificationText(specification) {
		return this._text(
			new TextRepresentationDimensionSpecificationVisitor({
				dimensionTree: this._dimensionTree,
			}).visit(specification),
		);
	}

	_textWithOptions({ _text, _options }) {
		return this._subclassResponsibility();
	}
}
